import timeUtils from '@/utils/timeUtils.ts';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { formatPrice } from '@/utils/formatters.ts';
import { TourStatus } from '@/features/dashboard/tours/types/TourStatus.ts';
import { RegistrationStatus } from '@/features/dashboard/tours/types/RegistrationStatus.ts';
import { TourPriceDto } from '@/features/dashboard/tourConfig/types/TourPriceDto.ts';
import { tourPriceDtoUtils } from '@/features/dashboard/tourConfig/types/TourPriceDto.utils.ts';
import { DropdownOptionDto } from '@/features/dashboard/tourCustomFields/types/DropdownCustomFieldDefinitionDto.ts';
import { statusConfig } from '@/features/dashboard/tours/types/TourStatus.utils.ts';
import { PhoneNumberDto } from '@/types/PhoneNumberDto.ts';
import { DestinationDto } from '@/features/dashboard/tours/types/DestinationDto.ts';
import { formatDestinationFull } from '@/features/dashboard/tours/types/DestinationDto.utils.ts';
import { registrationModeConfig } from '@/features/dashboard/tours/types/RegistrationDetails.ts.utils.ts';
import { RegistrationDetailDto } from '@/features/dashboard/tours/types/RegistrationDetailsDto.ts';
import { getEnumValueConfig } from '@/components/table/_fieldDefinitions/enum/enumRegistry.ts';

export const textExportRender = (value: unknown): unknown => {
  return value;
};

export const dateExportRender = (value: unknown): string => {
  if (typeof value === 'string') {
    return timeUtils.toDateString(value);
  }
  return '';
};

export const dateTimeExportRender = (value: unknown): string => {
  if (typeof value === 'string') {
    return timeUtils.toDateTimeString(value);
  }
  return '';
};

export const checkboxExportRender = (value: unknown): string => {
  if (typeof value === 'boolean') {
    return value ? 'Tak' : 'Nie';
  }
  return String(value ?? '');
};

export const tourStatusExportRender = (value: unknown): string => {
  const status = value as TourStatus;
  return statusConfig[status].label;
};

export const tourPriceArrayExportRender = (value: unknown): string => {
  if (!Array.isArray(value)) {
    return '';
  }
  return tourPriceDtoUtils.getTourPriceDisplayString(value as TourPriceDto[]);
};

export const moneyValueExportRender = (value: unknown): string => {
  const moneyValue = value as MoneyValueDto;
  if (!moneyValue) return '';
  return formatPrice(moneyValue.amount, moneyValue.currency);
};

export const nestedClientExportRender = (value: unknown): string => {
  const client = value as { firstName: string; lastName: string } | null;
  if (!client) return '';
  return `${client.firstName} ${client.lastName}`;
};

export const nestedTourExportRender = (value: unknown): string => {
  const tour = value as { name: string } | null;
  if (!tour) return '';
  return tour.name;
};

export const nestedBookingExportRender = (value: unknown): string => {
  const booking = value as { bookingCode?: string } | null | undefined;
  return booking?.bookingCode ?? '';
};

export const nestedParticipantExportRender = (value: unknown): string => {
  const participant = value as { firstName: string; lastName: string } | null;
  if (!participant) return '';
  return `${participant.firstName} ${participant.lastName}`;
};

export const nestedWaitlistClientExportRender = (value: unknown): string => {
  const person = value as { firstName: string; lastName: string } | null;
  if (!person) return '';
  return `${person.firstName} ${person.lastName}`;
};

export const nestedAppUserExportRender = (value: unknown): string => {
  const user = value as { firstName: string | null; lastName: string } | null;
  if (!user) return '';
  return [user.firstName, user.lastName].filter(Boolean).join(' ');
};

export const nestedInsuranceExportRender = (value: unknown): string => {
  const insurance = value as { name: string } | null;
  if (!insurance) return '';
  return insurance.name;
};

export const nestedTransportVariantExportRender = (value: unknown): string => {
  const variant = value as { variantName: string } | null;
  if (!variant) return '';
  return variant.variantName;
};

export const nestedObjectListExportRender = (value: unknown): string => {
  if (!Array.isArray(value)) return '';
  return value
    .map((item) => (item as { name?: string } | null)?.name ?? '')
    .filter(Boolean)
    .join(', ');
};

export const multiselectExportRender = (
  value: string[],
  customFieldsContext?: { options: DropdownOptionDto[] },
): string => {
  if (!Array.isArray(value)) return value;
  if (!customFieldsContext) return value?.toString();
  return value
    .map((id) => {
      const option = customFieldsContext.options.find((opt) => opt.id === id);
      return option ? option.label : String(id);
    })
    .join(', ');
};

export const dropdownExportRender = (
  value: unknown,
  customFieldsContext?: { options: DropdownOptionDto[] },
): string => {
  if (customFieldsContext) {
    const optionId = value as string;
    const option = customFieldsContext.options.find(
      (opt) => opt.id === optionId,
    );
    return option ? option.label : String(optionId ?? '');
  }
  return String(value ?? '');
};

export const availabilityExportRender = (value: unknown): string => {
  const availability = value as {
    currentCount: number;
    limit: number | null;
  } | null;
  if (!availability) return '';

  const { currentCount, limit } = availability;

  if (limit === null) {
    return String(currentCount);
  }

  return `${currentCount}/${limit}`;
};

export const dateRangeExportRender = (value: unknown): string => {
  const dateRange = value as { start: string; end: string } | null;
  if (!dateRange) return '';

  return timeUtils.toDateRangeString(dateRange.start, dateRange.end);
};

export const nestedInstallmentDetailsExportRender = (
  value: unknown,
): string => {
  const details = value as {
    installmentId: string | null;
    currency: string;
    orderNumber: number | null;
  } | null;
  if (!details || details.installmentId === null) return '';
  return `Rata ${details.orderNumber}`;
};

export const tourDestinationExportRender = (value: unknown): string => {
  const destination = value as DestinationDto | null;
  if (!destination) return '';
  return formatDestinationFull(destination);
};

export const phoneNumberExportRender = (value: unknown): string => {
  const phoneNumber = value as PhoneNumberDto | null;
  if (!phoneNumber) return '';
  return `${phoneNumber.dialCode} ${phoneNumber.number}`;
};

export const registrationModeExportRender = (value: unknown): string => {
  // Backend oddaje w tym polu całe `RegistrationDetailDto`; sam enum przyjmujemy
  // na wypadek, gdyby kiedyś przyszedł spłaszczony — pomyłka tutaj wywracała
  // eksport całej tabeli.
  const status =
    typeof value === 'string'
      ? (value as RegistrationStatus)
      : (value as RegistrationDetailDto | null)?.registrationStatus;
  if (!status) return '';

  return registrationModeConfig[status]?.label ?? '';
};

export const enumExportRender = (
  value: unknown,
  customFieldsContext?: { enumSimpleName: string },
): string => {
  const enumKey = value as string;
  if (!enumKey) return '';
  if (!customFieldsContext) return String(enumKey);
  const valueConfig = getEnumValueConfig(
    customFieldsContext.enumSimpleName,
    enumKey,
  );
  return valueConfig?.label ?? String(enumKey);
};
